(() => {
  'use strict';
  if (customElements.get('unik-product-description')) return;

  // Use HTML boundaries, not visual wrapping: a paragraph or <br> defines a line.
  const blocks = new Set(['P', 'DIV', 'SECTION', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
    'UL', 'OL', 'LI', 'BLOCKQUOTE', 'TABLE', 'TR', 'TD', 'TH', 'PRE']);
  const linesIn = (root) => {
    const lines = [];
    let parts = [];
    const flush = () => {
      if (parts.some((node) => node.textContent.trim())) {
        lines.push({ text: parts.map((node) => node.textContent).join('').trim(),
          first: parts[0], last: parts[parts.length - 1] });
      }
      parts = [];
    };
    const visit = (node) => {
      if (node.nodeType === Node.TEXT_NODE) { parts.push(node); return; }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      if (['SCRIPT', 'STYLE', 'TEMPLATE'].includes(node.tagName)) return;
      const boundary = blocks.has(node.tagName) || node.tagName === 'BR';
      if (boundary) flush();
      node.childNodes.forEach(visit);
      if (boundary) flush();
    };
    root.childNodes.forEach(visit);
    flush();
    return lines;
  };

  const rangeFor = (first, last, firstOffset = 0, lastOffset = last.length) => {
    const range = document.createRange();
    range.setStart(first, firstOffset);
    range.setEnd(last, lastOffset);
    return range;
  };
  const formattedLine = (line, root) => {
    const range = rangeFor(line.first, line.last, line.firstOffset, line.lastOffset);
    let fragment = range.cloneContents();
    let ancestor = range.commonAncestorContainer;
    if (ancestor.nodeType === Node.TEXT_NODE) ancestor = ancestor.parentElement;
    // cloneContents omits its common ancestor; restore inline/block formatting.
    while (ancestor !== root) {
      const wrapper = ancestor.cloneNode(false);
      wrapper.removeAttribute('id');
      wrapper.append(fragment);
      fragment = wrapper;
      ancestor = ancestor.parentElement;
    }
    return fragment;
  };
  const materialList = (line, root) => {
    const holder = document.createElement('div');
    holder.append(formattedLine(line, root));
    const walker = document.createTreeWalker(holder, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let offset = 0;
    while (walker.nextNode()) {
      const node = walker.currentNode;
      nodes.push({ node, start: offset, end: offset + node.length });
      offset += node.length;
    }
    const list = document.createElement('ul');
    // Split on commas, except a comma between two digits: "76,8% Viskose" is one
    // item with a Norwegian decimal comma, not "76" and "8% Viskose" (UNIK: F6-03).
    const segments = [];
    for (const match of holder.textContent.matchAll(/[^,]+/g)) {
      const previous = segments[segments.length - 1];
      if (previous && /\d$/.test(previous.raw) && /^\d/.test(match[0])) {
        previous.raw += ',' + match[0];
        continue;
      }
      segments.push({ index: match.index, raw: match[0] });
    }
    for (const { index, raw } of segments) {
      const text = raw.trim();
      if (!text) continue;
      const start = index + raw.indexOf(text);
      const end = start + text.length;
      const first = nodes.find((part) => part.end > start);
      const last = nodes.find((part) => part.end >= end);
      const item = document.createElement('li');
      item.append(formattedLine({ first: first.node, last: last.node,
        firstOffset: start - first.start, lastOffset: end - last.start }, holder));
      list.append(item);
    }
    return list;
  };
  const emptyText = (node) => node.nodeType === Node.TEXT_NODE && !node.textContent.trim();
  const expandBoundary = (node, root, direction) => {
    while (node.parentNode !== root) {
      let sibling = node[direction];
      while (sibling && emptyText(sibling)) sibling = sibling[direction];
      if (sibling) break;
      node = node.parentNode;
    }
    return node;
  };
  const tidy = (root) => {
    // Remove wrappers emptied by extraction, preserving images and other media.
    Array.from(root.querySelectorAll('p, div, span, strong, b, em, i, h1, h2, h3, h4, h5, h6'))
      .reverse().forEach((node) => {
        if (!node.textContent.trim() && !node.querySelector('img, svg, video, audio, iframe, input, hr')) node.remove();
      });
    [root, ...root.querySelectorAll('p, div')].forEach((node) => {
      for (const edge of ['firstChild', 'lastChild']) {
        while (node[edge] && (emptyText(node[edge]) || node[edge].nodeName === 'BR')) node[edge].remove();
      }
    });
  };
  const inHeading = (line) => Boolean(line.first.parentElement.closest('h1, h2, h3, h4, h5, h6'));
  // The inline content of a line: formattedLine without the block wrappers (p, div, li, ...) it rebuilds.
  const inlineOf = (line, root) => {
    let fragment = document.createDocumentFragment();
    fragment.append(formattedLine(line, root));
    const sole = () => {
      const nodes = Array.from(fragment.childNodes).filter((node) => !emptyText(node));
      return nodes.length === 1 && nodes[0].nodeType === Node.ELEMENT_NODE && blocks.has(nodes[0].tagName) ? nodes[0] : null;
    };
    for (let wrapper = sole(); wrapper; wrapper = sole()) {
      const inner = document.createDocumentFragment();
      inner.append(...wrapper.childNodes);
      fragment = inner;
    }
    for (const edge of ['firstChild', 'lastChild']) {
      while (fragment[edge] && (emptyText(fragment[edge]) || fragment[edge].nodeName === 'BR')) fragment[edge].remove();
    }
    return fragment;
  };

  // "Materialer"/"Materiale": the next line, split on commas (F4-03).
  const materialsHeading = /^materialer?\s*:?$/iu;
  const takeMaterials = (lines, start) => (lines[start] && !inHeading(lines[start]) ? [lines[start]] : []);
  const buildMaterials = (taken, root) => materialList(taken[0], root);

  // "Mål": every following line that reads as a measurement, until the first that does not.
  const measurementsHeading = /^(?:produkt|plagg)?mål\s*(?:\(cm\))?\s*:?$/iu;
  const otherHeadings = /^(?:materialer?|vedlikehold|vaskeanvisning|vask|pleie|detaljer|passform|info(?:rmasjon)?)\s*:?$/iu;
  const size = String.raw`(?:xxs|xs|s|m|l|xl|xxl|xxxl|[2-6]xl|one\s*size|onesize|os|\d{2,3})`;
  // "S/M", "L/XL:", "Størrelse S/M", "Str. M", "One size", "36/38", "XS-S".
  const sizeLabel = new RegExp(String.raw`^(?:st(?:r\.?|ørrelse|orrelse)?\s*:?\s*)?${size}(?:\s*[/–-]\s*${size})*\s*:?$`, 'iu');
  const unitValue = /\d[\d.,]*\s*(?:cm|mm|m|meter)\b/iu; // "51 cm.", "8.5cm", "16,5 cm", "65x180cm"
  const dimension = /\d\s*[x×]\s*\d/iu; // "10X10X5", "65x180"
  const labelled = /^[^:\d]{2,40}:+\s*(?:ca\.?\s*)?\d/iu; // "Lengde:: 69", "Brystmål: ca. 128"
  // A sentence that happens to contain a length ("Modellen er 175 cm og bruker S/M") is not a measurement.
  const prose = /\b(?:er|har|bruker|vaskes|passer|kan|bør|anbefaler|modellen)\b/iu;
  const isMeasurementLine = (text) =>
    sizeLabel.test(text) || labelled.test(text) || ((unitValue.test(text) || dimension.test(text)) && !prose.test(text));
  const takeMeasurements = (lines, start) => {
    const taken = [];
    for (let index = start; index < lines.length; index += 1) {
      const line = lines[index];
      if (inHeading(line) || otherHeadings.test(line.text) || measurementsHeading.test(line.text) || !isMeasurementLine(line.text)) break;
      taken.push(line);
    }
    return taken;
  };
  // One <p> per size group: <p><strong>S/M</strong><br>Bryst: 51 cm<br>Lengde: 69 cm</p>.
  const buildMeasurements = (lines, root) => {
    const fragment = document.createDocumentFragment();
    let group = null;
    for (const line of lines) {
      if (sizeLabel.test(line.text)) {
        group = document.createElement('p');
        const label = document.createElement('strong');
        label.className = 'unik-product-measurements__size';
        label.textContent = line.text.replace(/\s*:$/, '');
        group.append(label);
        fragment.append(group);
        continue;
      }
      if (!group) {
        group = document.createElement('p');
        fragment.append(group);
      }
      if (group.childNodes.length) group.append(document.createElement('br'));
      group.append(inlineOf(line, root));
    }
    return fragment;
  };

  // Moves the heading line and the lines `take` selects from the body into `destination`,
  // then shows the closed accordion. Leaves the body unchanged when nothing is taken, the
  // content is empty, or the selection contains media that the line scan does not see.
  const extractSection = (body, { heading, take, build, details, destination }) => {
    if (!details || !destination) return false;
    const lines = linesIn(body);
    const index = lines.findIndex((line) => heading.test(line.text));
    if (index < 0) return false;
    const taken = take(lines, index + 1);
    if (!taken.length) return false;
    const content = build(taken, body);
    if (!content || !content.childNodes.length) return false;
    const last = taken[taken.length - 1];
    const selection = rangeFor(lines[index].first, last.last);
    selection.setStartBefore(expandBoundary(lines[index].first, body, 'previousSibling'));
    selection.setEndAfter(expandBoundary(last.last, body, 'nextSibling'));
    if (selection.cloneContents().querySelector('img, svg, video, audio, iframe, table')) return false;
    destination.append(content);
    selection.deleteContents();
    tidy(body);
    details.open = false;
    details.hidden = false;
    return true;
  };

  customElements.define('unik-product-description', class extends HTMLElement {
    connectedCallback() {
      // Deferred scripts run after parsing; a microtask also supports AJAX inserts.
      queueMicrotask(() => this.extract());
    }

    extract() {
      if (this.dataset.processed) return;
      const body = this.querySelector('[data-product-description]');
      if (!body) return;
      this.dataset.processed = 'true';
      // Plain-text descriptions can also use literal line breaks.
      if (!body.children.length && /\r?\n/.test(body.textContent.trim())) {
        const textLines = body.textContent.trim().split(/\r?\n/);
        body.replaceChildren();
        textLines.forEach((text, index) => {
          if (index) body.append(document.createElement('br'));
          body.append(document.createTextNode(text));
        });
      }
      // Each extraction re-reads the lines, so the second never sees nodes the first removed.
      extractSection(body, {
        heading: materialsHeading, take: takeMaterials, build: buildMaterials,
        details: this.querySelector('details.unik-product-materials'),
        destination: this.querySelector('[data-product-materials]'),
      });
      extractSection(body, {
        heading: measurementsHeading, take: takeMeasurements, build: buildMeasurements,
        details: this.querySelector('details.unik-product-measurements'),
        destination: this.querySelector('[data-product-measurements]'),
      });
    }
  });
})();
